import throttle from '../../../assets/js/base/_throttle.js';

export class TableOfContents {
	/**
	 * Initialize a new TableOfContents element
	 *
	 * @param {HTMLElement} node - the node that contains the root scrolling element
	 * @param {object} opts -
	 * 	- mode: snap, normal
	 */
	constructor( node, opts ) {
		this.node = node;
		this.opts = {
			mode: 'snap', // snap to next item or free scroll
			back: '.table-of-contents__scroll--back',
			forward: '.table-of-contents__scroll--forward',
			content: '.table-of-contents__content',
			items: '.table-of-contents__item',
			link: '.table-of-contents__link',
			...opts,
		};

		if ( ! this.node ) {
			return;
		}

		this.interval = null;
		this.back = this.node.querySelector( this.opts.back );
		this.forward = this.node.querySelector( this.opts.forward );
		this.content = this.node.querySelector( this.opts.content );

		this.items = Array.from( this.node.querySelectorAll( this.opts.items ) ).map( ( node ) => {
			const link = node.querySelector( this.opts.link );
			let id = null;
			let target = null;

			if ( link?.hash ) {
				id = link.hash.slice( 1 );
				target = document.getElementById( id );
			}

			return {
				node,
				id,
				link,
				target,
			};
		} );

		window.addEventListener( 'resize', this.setState.bind( this ) );
		this.content.addEventListener( 'scroll', this.setState.bind( this ) );

		if ( 'snap' === this.opts.mode ) {
			if ( this.forward ) {
				this.forward.addEventListener( 'click', this.forwardIndex.bind( this ) );
			}

			if ( this.back ) {
				this.back.addEventListener( 'click', this.backIndex.bind( this ) );
			}
		} else {
			if ( this.forward ) {
				this.forward.addEventListener( 'pointerdown', this.scrollForward.bind( this ) );
				this.forward.addEventListener( 'pointerup', this.scrollStop.bind( this ) );
			}

			if ( this.back ) {
				this.back.addEventListener( 'pointerdown', this.scrollBack.bind( this ) );
				this.back.addEventListener( 'pointerup', this.scrollStop.bind( this ) );
			}
		}

		let initialIndex = window.location.hash ? Math.max( 0, this.items.map( ( item ) => item.id ).indexOf( window.location.hash.slice( 1 ) ) ) : 0;

		this.setIndex( initialIndex );
		this.setState();
	}

	/**
	 * Scroll forward the scrollable content area
	 */
	scrollForward() {
		// Calculate scroll increment based on content width (0.5% of width)
		const scrollIncrement = Math.max( 5, Math.floor( this.content.clientWidth * 0.005 ) );

		this.scrollInterval = setInterval( () => {
			this.content.scrollTo( { left: this.content.scrollLeft + scrollIncrement } );

			if ( this.forward.disabled ) {
				this.scrollStop();
			}
		}, 10 );
	}

	/**
	 * Scroll backwards the scrollable content area
	 */
	scrollBack() {
		// Calculate scroll increment based on content width (0.5% of width)
		const scrollIncrement = Math.max( 5, Math.floor( this.content.clientWidth * 0.005 ) );

		this.scrollInterval = setInterval( () => {
			this.content.scrollTo( { left: Math.max( 0, this.content.scrollLeft - scrollIncrement ) } );

			// Check if we've reached the beginning
			if ( this.content.scrollLeft <= 0 ) {
				if ( this.back ) {
					this.back.disabled = true;
				}
				this.scrollStop();
			}
		}, 10 );
	}

	/**
	 * Stop the scrolling interval if it's active
	 */
	scrollStop() {
		if ( typeof this.scrollInterval === 'number' ) {
			clearInterval( this.scrollInterval );
		}

		this.scrollInterval = null;
	}

	setState() {
		if ( ! this.items.length ) {
			return;
		}

		const contentRect = this.content.getBoundingClientRect();

		// check the first visible element in the content area (label or first item)
		const firstElementInContent = this.content.firstElementChild;
		let firstElementLeft = 0;

		if ( firstElementInContent ) {
			const firstElementRect = firstElementInContent.getBoundingClientRect();
			firstElementLeft = firstElementRect.left - contentRect.left;
		}

		// check first element visibility (back button logic)
		if ( firstElementLeft >= 0 ) {
			if ( this.back ) {
				this.back.disabled = true;
			}
			this.node.classList.remove( 'table-of-contents--fade-in' );
		} else {
			if ( this.back ) {
				this.back.removeAttribute( 'disabled' );
			}
			this.node.classList.add( 'table-of-contents--fade-in' );
		}

		// forward button logic
		const maxScrollLeft = this.content.scrollWidth - contentRect.width;
		const currentScrollLeft = this.content.scrollLeft;

		// Fix: Use consistent approach for fade effects (both add/remove classes in the same way)
		if ( currentScrollLeft >= maxScrollLeft - 2 ) {
			if ( this.forward ) {
				this.forward.disabled = true;
			}
			this.node.classList.remove( 'table-of-contents--fade-out' );
		} else {
			if ( this.forward ) {
				this.forward.removeAttribute( 'disabled' );
			}
			this.node.classList.add( 'table-of-contents--fade-out' );
		}

		if ( this.content.scrollWidth > contentRect.width ) {
			this.node.classList.add( 'table-of-contents--scrollable' );
		} else {
			this.node.classList.remove( 'table-of-contents--scrollable' );
		}
	}

	getIndex() {
		if ( ! this.items.length ) {
			return -1; // Fixed: Return -1 if no items to indicate no selection
		}

		const contentRect = this.content.getBoundingClientRect();

		// calculate the center point of the visible content area
		const visibleCenterX = this.content.scrollLeft + contentRect.width / 2;

		// find the item whose center is closest to the center of the visible area
		const itemCenters = this.items.map( ( item ) => {
			if ( ! item.link ) return { index: -1, distance: Infinity };

			const itemRect = item.link.getBoundingClientRect();
			const itemLeft = itemRect.left - contentRect.left + this.content.scrollLeft;
			const itemCenter = itemLeft + itemRect.width / 2;

			return {
				index: this.items.indexOf( item ),
				distance: Math.abs( itemCenter - visibleCenterX ),
			};
		} );

		// sort by distance and get the closest item's index
		itemCenters.sort( ( a, b ) => a.distance - b.distance );
		return itemCenters[ 0 ]?.index ?? 0;
	}

	backIndex() {
		const currentIndex = this.getIndex();

		if ( currentIndex === -1 ) {
			return; // No items available
		}

		if ( currentIndex <= 0 ) {
			// if we're already at the first item, scroll to the very beginning
			// this will show the label
			this.content.scrollTo( {
				left: 0,
				behavior: 'smooth',
			} );
		} else {
			// otherwise, navigate to the previous valid link item
			let prevIndex = currentIndex - 1;
			while ( prevIndex >= 0 ) {
				if ( this.items[ prevIndex ].link ) {
					break;
				}
				prevIndex--;
			}

			if ( prevIndex >= 0 ) {
				this.setIndex( prevIndex );
			}
		}
	}

	forwardIndex() {
		const currentIndex = this.getIndex();

		if ( currentIndex === -1 ) {
			return; // No items available
		}

		// find the next valid item that has a link
		let nextIndex = currentIndex + 1;
		let maxIterations = this.items.length; // Prevent infinite loop
		let iterations = 0;

		while ( nextIndex < this.items.length && iterations < maxIterations ) {
			if ( this.items[ nextIndex ].link ) {
				break;
			}
			nextIndex++;
			iterations++;
		}

		// if we found a valid next item, navigate to it
		if ( nextIndex < this.items.length ) {
			this.setIndex( nextIndex );
		}
	}

	setIndex( index, behavior = 'smooth' ) {
		if ( ! this.items.length || index < 0 || index >= this.items.length ) {
			return;
		}

		const item = this.items[ index ];

		// check if the item has a valid link
		if ( ! item.link ) {
			// find the next valid item
			let nextValidIndex = -1;
			let maxIterations = this.items.length; // Prevent infinite loop

			for ( let i = index + 1; i < this.items.length && maxIterations > 0; i++, maxIterations-- ) {
				if ( this.items[ i ].link ) {
					nextValidIndex = i;
					break;
				}
			}

			if ( nextValidIndex >= 0 ) {
				this.setIndex( nextValidIndex, behavior );
			}
			return;
		}

		const contentRect = this.content.getBoundingClientRect();
		const itemRect = item.link.getBoundingClientRect();

		// calculate position to center the item in the viewport
		const itemCenter = itemRect.left + itemRect.width / 2 - contentRect.left;
		const targetScrollLeft = this.content.scrollLeft + itemCenter - contentRect.width / 2;

		this.content.scrollTo( {
			left: targetScrollLeft,
			behavior,
		} );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	for ( const node of document.querySelectorAll( '.table-of-contents' ) ) {
		new TableOfContents( node, { ...node.dataset } );
	}
} );
